import * as R from 'ramda';
import { amountFormat } from 'utils/amount-format';
import { asyncHelper } from 'utils/asyncHelper';
import { CommonModule } from 'utils/CommonModule';
import { ModuleHelper } from 'utils/moduleHelper';
import { reduxHelper } from 'utils/reduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';

import { GlobalState } from 'reactApp/core/rootReducer';
import { getBillingApi } from 'reactApp/modules/BillingModule/BillingApi';
import {
    BillingInfo,
    BillingSaveIndividualData,
    BillingSaveLegalData,
    LegalForm,
} from 'reactApp/modules/BillingModule/BillingModule.types';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule';

const getModule = ModuleHelper.wrapMultimodule(
    ({ pid, clientPid }: { pid: string; clientPid?: string }) => {
        const apiMethods = getBillingApi(pid, clientPid);

        const {
            Actions: { loadData },
            Selectors: { getData, getHasError, getIsLoaded, getIsLoading },
        } = CommonModule.createCommonModule<BillingInfo | undefined>({
            apiMethods,
            entityName: 'billing',
            entityNamePublic: 'биллинг',
            key: { pid, clientPid },
        });

        const Actions = {
            loadData,
            loadMoreInfo: () => () => apiMethods.loadMoreInfo(),
            searchInn: (inn: string, legalForm?: LegalForm) => () =>
                apiMethods.searchInn(inn, legalForm),
            pay: (amount: string) => () => apiMethods.pay(amount),
            saveLegalInfo: (legalInfo: BillingSaveLegalData) => () =>
                apiMethods.saveLegalInfo(legalInfo),
            saveIndividualInfo: (
                individualInfo: BillingSaveIndividualData,
            ) => () => apiMethods.saveIndividualInfo(individualInfo),
        };

        const getBalance = R.pipe(getData, R.propOr(0, 'balance'));

        const getBonus = (state) => getData(state)?.bonus || 0;

        const getAutopay = (state) => getData(state)?.autopay || false;

        const getLegalForm = (state) =>
            getData(state)?.legal_form || LegalForm.unknown;

        const getTotalBalance: ReduxTypeUtils.Selector<number> = R.converge(
            R.pipe(Array, R.sum as any),
            [getBalance, getBonus],
        );

        const getTotalBalanceFormatted = R.pipe(
            getTotalBalance,
            amountFormat,
            (numStr) => `${numStr} ₽`,
        );

        const getIsPostpaid = R.pipe(getData, (data) =>
            data ? data.postpaid : false,
        );

        const getIsNonZeroBalance = R.pipe(
            getData,
            (data) => data && Number(data.balance) !== 0,
        );

        const getHasPayments = R.pipe(getData, (data) =>
            Boolean(data?.extra?.first_pay_dmr || data?.extra?.first_pay_order),
        );

        const Selectors = {
            getData,

            getHasError,
            getIsLoaded,
            getIsLoading,

            getBalance,
            getAutopay,
            getBonus,
            getLegalForm,
            getTotalBalance,
            getTotalBalanceFormatted,
            getIsPostpaid,
            getIsNonZeroBalance,
            getHasPayments,
        };

        return { Actions, Selectors };
    },
);

export const getBillingActions = (pid: string, clientPid?: string) =>
    getModule({ pid, clientPid }).Actions;
export const getBillingSelectors = (pid: string, clientPid?: string) =>
    getModule({ pid, clientPid }).Selectors;

export const getBoundBillingActions = (pid: string, clientPid?: string) => {
    return reduxHelper.bindActions(getBillingActions(pid, clientPid));
};

export const getBoundBillingSelectors = (pid: string, clientPid?: string) => {
    return reduxHelper.bindActions(getBillingSelectors(pid, clientPid));
};

// Current user billing

const loadData = () => (dispatch, getState) => {
    const pid = CurrentUserSelectors.getPid(getState());
    return dispatch(getBillingActions(pid).loadData());
};

const loadMoreInfo = () => (dispatch, getState) => {
    const pid = CurrentUserSelectors.getPid(getState());
    return dispatch(getBillingActions(pid).loadMoreInfo());
};

const pay = (amount: number) => (dispatch, getState) => {
    const pid = CurrentUserSelectors.getPid(getState());
    return dispatch(getBillingActions(pid).pay(amount.toFixed(2)));
};

const saveLegalInfo = (legalInfo: BillingSaveLegalData) => (
    dispatch,
    getState,
) => {
    const pid = CurrentUserSelectors.getPid(getState());
    return dispatch(getBillingActions(pid).saveLegalInfo(legalInfo));
};

export const BillingActions = {
    loadData,
    loadMoreInfo,
    pay,
    saveLegalInfo,
    refreshBalance: () => async (dispatch, getState) => {
        const refreshTime = 60 * 1000;
        const refreshPeriod = 10 * 1000;
        const startDate = new Date().getTime();
        const oldBalance = BillingSelectors.getTotalBalance(getState());
        let newBalance = oldBalance;

        while (
            oldBalance === newBalance &&
            new Date().getTime() < startDate + refreshTime
        ) {
            try {
                await dispatch(BillingActions.loadData());
                newBalance = BillingSelectors.getTotalBalance(getState());
                if (newBalance > oldBalance) {
                    return 'increase';
                } else if (newBalance < oldBalance) {
                    return 'decrease';
                }
            } finally {
                await asyncHelper.delay(refreshPeriod);
            }
        }
        return false;
    },
};

const getData = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getData(state);
};

const getIsLoaded = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getIsLoaded(state);
};

const getIsLoading = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getIsLoading(state);
};

const getHasError = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getHasError(state);
};

const getTotalBalance = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getTotalBalance(state);
};

const getTotalBalanceFormatted = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getTotalBalanceFormatted(state);
};

const getIsPostpaid = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getIsPostpaid(state);
};

const getIsNonZeroBalance = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getIsNonZeroBalance(state);
};

const getHasPayments = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getHasPayments(state);
};

const getBonus = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getBonus(state);
};

const getBalance = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getBalance(state);
};

const getLegalForm = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getLegalForm(state);
};
const getAutopay = (state: GlobalState) => {
    const pid = CurrentUserSelectors.getPid(state);
    return getBillingSelectors(pid).getAutopay(state);
};

export const BillingSelectors = {
    getData,
    getIsLoaded,
    getIsLoading,
    getHasError,

    getBonus,
    getAutopay,
    getTotalBalance,
    getTotalBalanceFormatted,
    getIsPostpaid,
    getIsNonZeroBalance,
    getHasPayments,
    getBalance,
    getLegalForm,
};

export const BoundBillingActions = reduxHelper.bindActions(BillingActions);
export const BoundBillingSelectors = reduxHelper.bindActions(BillingSelectors);
